import React, { useState } from 'react';
import type { MarketplaceLead } from '@gcv/shared';
import { MarketplaceLeadStatus } from '@gcv/shared';
import { Box } from '@mui/system';

import { useBankMarketplaceLeads } from 'hooks/queries';
import { useComponent } from 'hooks/useComponent';
import { getAppViewStateStore } from 'stores/AppViewStateStore';
import { getFiBankStore } from 'stores/FiBankStore';
import type { Column, Row } from 'ui';
import { Spinner, Table, TableContainer, Text, Toggle, ZeroState } from 'ui';
import { DateTimeHelpers } from 'util/dateTime.util';
import useQueryString from 'util/queryString.util';
import { LeadDrawer } from '../components/lead-drawer';

export const Leads: React.FC = useComponent(() => {
  const { bankMarketplaceLeads, isLoadingBankMarketplaceLeads } = useBankMarketplaceLeads();

  const bankStore = getFiBankStore();
  const appViewStore = getAppViewStateStore();

  const [leadId, setLeadId] = useQueryString('leadId', '');
  const [leadDrawerOpen, setLeadDrawerOpen] = useState<boolean>(leadId != null);

  const columns: Column<MarketplaceLead>[] = [
    {
      label: 'Name',
      id: 'user_name',
      widthPercent: 15
    },
    {
      label: 'Email',
      id: 'user_email',
      widthPercent: 25
    },
    {
      label: 'Phone',
      id: 'user_phone',
      widthPercent: 15
    },
    {
      label: 'Company',
      id: 'company_name',
      widthPercent: 25
    },
    {
      label: 'Status',
      id: 'status',
      widthPercent: 10
    },
    {
      label: 'Date Created',
      id: 'date_created',
      widthPercent: 10,
      format: (lead) => {
        return DateTimeHelpers.formatISOToTableDateString(lead.date_created, bankStore.bank.iana_timezone);
      }
    }
  ];

  const tableRows: Row<MarketplaceLead>[] = (bankMarketplaceLeads ?? [])
    ?.filter(
      (l) =>
        appViewStore.showArchivedLeads ||
        ![
          MarketplaceLeadStatus.Not_A_Fit,
          MarketplaceLeadStatus.Unqualified,
          MarketplaceLeadStatus.Invited
        ].includes(l.status)
    )
    .map((l) => {
      return { id: l.id, data: l };
    });

  if (!bankMarketplaceLeads || isLoadingBankMarketplaceLeads) {
    return <Spinner />;
  }

  if (bankMarketplaceLeads.length === 0) {
    return (
      <ZeroState
        title={'You have no leads at the moment'}
        subTitle={'Create a Marketplace Offer to start getting leads.'}
        icon={'/img/empty-states/review-in-progress-account.svg'}
      />
    );
  }

  return (
    <>
      <Box>
        <Box sx={{ display: 'flex', padding: 1, justifyContent: 'flex-end', marginTop: -4 }}>
          <Toggle
            checked={appViewStore.showArchivedLeads}
            onClick={() => (appViewStore.showArchivedLeads = !appViewStore.showArchivedLeads)}
          />
          <Text content="Show Archived Leads" type="body3" />
        </Box>

        {tableRows.length === 0 ? (
          <ZeroState
            title={'You have no open leads at the moment'}
            subTitle={'Select the "Show Archived Leads" toggle to view your previous leads'}
            icon={'/img/empty-states/review-in-progress-account.svg'}
          />
        ) : (
          <TableContainer>
            <Table
              columns={columns}
              rows={tableRows}
              dataCy="bank-marketplace-leads-table"
              defaultSort={appViewStore.accountPaginationFilters.leads.defaultSort as keyof MarketplaceLead}
              defaultSortDirection={appViewStore.accountPaginationFilters.leads.defaultSortDirection}
              onSort={(sort, sortDirection) => {
                appViewStore.accountPaginationFilters.leads.defaultSort = sort;
                appViewStore.accountPaginationFilters.leads.defaultSortDirection = sortDirection;
              }}
              defaultRowCount={appViewStore.accountPaginationFilters.leads.pageSize}
              onRowsPerPageChange={(pageSize: number) => {
                appViewStore.accountPaginationFilters.leads.pageSize = pageSize;
              }}
              page={appViewStore.accountPaginationFilters.leads.page}
              onPageChange={(page) => {
                appViewStore.accountPaginationFilters.leads.page = page;
              }}
              onClick={(id) => {
                setLeadId(id);
                setLeadDrawerOpen(true);
              }}
              clickable
            />
          </TableContainer>
        )}
      </Box>
      {leadId && (
        <LeadDrawer leadId={leadId} isOpen={leadDrawerOpen} onClose={() => setLeadDrawerOpen(false)} />
      )}
    </>
  );
});
